import { createFileRoute, useNavigate, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import { CircularLoader } from '@/components/CircularLoader';
import { api } from '@/lib/api';
import styles from './auth.module.scss';

export const Route = createFileRoute('/signup')({
  beforeLoad: ({ context }: { context: any }) => {
    // If user is already logged in, redirect to home
    if (context.authLoading) return;
    if (context.user) {
      throw redirect({ to: '/' });
    }
  },
  component: SignUpPage,
});

/**
 * Validate display name: 3-30 chars, alphanumeric + underscores/hyphens only
 */
function validateDisplayName(displayName: string): string | null {
  if (displayName.length < 3) {
    return 'Username must be at least 3 characters';
  }
  if (displayName.length > 30) {
    return 'Username must be at most 30 characters';
  }
  if (!/^[a-zA-Z0-9_-]+$/.test(displayName)) {
    return 'Username can only contain letters, numbers, underscores, and hyphens';
  }
  return null;
}

function SignUpPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email || !displayName || !firstName || !lastName || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    // Validate display name format
    const displayNameError = validateDisplayName(displayName);
    if (displayNameError) {
      setError(displayNameError);
      return;
    }

    // Validate first name
    if (firstName.length > 50) {
      setError('First name must be at most 50 characters');
      return;
    }

    // Validate last name
    if (lastName.length > 50) {
      setError('Last name must be at most 50 characters');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    // Step 1: Sign up with Firebase
    const { error: signUpError } = await signUp(email, password);

    if (signUpError) {
      setLoading(false);
      // Handle Firebase-specific errors
      const firebaseError = signUpError as any;
      if (firebaseError.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else if (firebaseError.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password');
      } else if (firebaseError.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError(signUpError.message);
      }
      return;
    }

    // Step 2: Create user profile with display name and name fields
    try {
      await api.createUserProfile({
        display_name: displayName,
        first_name: firstName,
        last_name: lastName,
      });
    } catch (profileError) {
      setLoading(false);
      // Handle profile creation errors
      if (profileError instanceof Error) {
        if (profileError.message.includes('Username already taken')) {
          setError('Username is already taken. Please choose a different one.');
        } else {
          setError(`Failed to create profile: ${profileError.message}`);
        }
      } else {
        setError('Failed to create profile. Please try again.');
      }
      return;
    }

    setLoading(false);
    setSuccess(true);

    // Navigate to login after a brief delay
    setTimeout(() => {
      navigate({ to: '/login' });
    }, 2000);
  };

  return (
    <div className={styles.authContainer}>
      <motion.div
        className={styles.authCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className={styles.authCard__header}>
          <h1 className={styles.authCard__title}>Create Account</h1>
          <p className={styles.authCard__subtitle}>
            Sign up to start building your daily review decks
          </p>
        </div>

        {success ? (
          <motion.div
            className={styles.authCard__success}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className={styles.authCard__successIcon}>✓</div>
            <p>Account created successfully!</p>
            <p className={styles.authCard__successSubtext}>
              Redirecting to login...
            </p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.authCard__form}>
            <div className={styles.authCard__field}>
              <label htmlFor="email" className={styles.authCard__label}>
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.authCard__input}
                placeholder="you@example.com"
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className={styles.authCard__field}>
              <label htmlFor="displayName" className={styles.authCard__label}>
                Username
              </label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={styles.authCard__input}
                placeholder="your_username"
                disabled={loading}
                autoComplete="username"
              />
              <span className={styles.authCard__hint}>
                3-30 characters. Letters, numbers, underscores, and hyphens only.
              </span>
            </div>

            <div className={styles.authCard__field}>
              <label htmlFor="firstName" className={styles.authCard__label}>
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={styles.authCard__input}
                placeholder="John"
                disabled={loading}
                autoComplete="given-name"
              />
            </div>

            <div className={styles.authCard__field}>
              <label htmlFor="lastName" className={styles.authCard__label}>
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={styles.authCard__input}
                placeholder="Doe"
                disabled={loading}
                autoComplete="family-name"
              />
            </div>

            <div className={styles.authCard__field}>
              <label htmlFor="password" className={styles.authCard__label}>
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.authCard__input}
                placeholder="At least 6 characters"
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            <div className={styles.authCard__field}>
              <label htmlFor="confirmPassword" className={styles.authCard__label}>
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={styles.authCard__input}
                placeholder="Re-enter your password"
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            {error && (
              <motion.div
                className={styles.authCard__error}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
              >
                {error}
              </motion.div>
            )}

            <motion.button
              type="submit"
              className={styles.authCard__button}
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? (
                <span className={styles.authCard__buttonContent}>
                  <CircularLoader className={styles.authCard__loader} />
                  Creating Account...
                </span>
              ) : (
                'Sign Up'
              )}
            </motion.button>

            <div className={styles.authCard__footer}>
              Already have an account?{' '}
              <Link to="/login" className={styles.authCard__link}>
                Log in
              </Link>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
